import { NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';
import { CoreModule } from '@core/core.module';
import { NgxDaterangepickerMd } from 'ngx-daterangepicker-material';
import { EllipsisModule } from 'ngx-ellipsis';
import { IConfig, NgxMaskDirective, NgxMaskPipe, provideEnvironmentNgxMask } from 'ngx-mask';

import { ColumnDetailsComponent } from './column-details/column-details.component';
import {
	SpecialInstructionsDialog,
	SpecialInstructionsDialogComponent,
} from './special-instructions-dialog/special-instructions-dialog.component';
import { EditPlanComponent, EditPlanDialog } from './edit-plan/edit-plan.component';
import {
	BidMonitorLanesComponent,
	LanesDialog,
} from './bid-monitor-lanes/bid-monitor-lanes.component';
import { AddLaneComponent, AddLaneDialog } from './add-lane/add-lane.component';
import { CarrierLanesComponent } from './carrier-lanes/carrier-lanes.component';
import { DestinationsComponent } from './destinations/destinations.component';
import { LaneActionsComponent } from './lane-actions/lane-actions.component';
import { SearchTruckBarComponent } from './search-truck-bar/search-truck-bar.component';
import { DateRangeSelectorComponent } from './date-range-selector/date-range-selector.component';
import { McDotFlipAnimationComponent } from './mc-dot-flip-animation/mc-dot-flip-animation.component';
import { LoadingSpinnerDialogComponent } from './loading-spinner-dialog/loading-spinner-dialog.component';
import { GoogleLocationDirective } from './directives/google-location/google-location.directive';
import { NumbersInputDirective } from './directives/numbersInput/numbers-input.directive';
import { ProjectSelectorComponent } from './project-selector/project-selector.component';
import { CarrierAutocompleteComponent } from './carrier-autocomplete/carrier-autocomplete.component';
import { CarrierSearchComponent } from './carrier-search/carrier-search.component';
import { CarrierSelectorComponent } from './carrier-selector/carrier-selector.component';
import { CustomerSelectorComponent } from './customer-selector/customer-selector.component';
import { UserPopoverComponent } from './user-popover/user-popover.component';
import { CustomerPopoverComponent } from './customer-popover/customer-popover.component';
import { SetPasswordComponent } from './set-password/set-password.component';
import { PricingFeedbackDialogComponent } from './pricing-feedback-dialog/pricing-feedback-dialog.component';
import { SearchHorizontalListComponent } from './search-horizontal-list/search-horizontal-list.component';
import { DeadheadSlidersComponent } from './deadhead-sliders/deadhead-sliders.component';
import { ProjectPanelComponent } from './project-panel/project-panel.component';
import {
	RequestForProposalComponent,
	RFPDialog,
} from './request-for-proposal/request-for-proposal.component';
import { CarrierSearchPopoverComponent } from './carrier-search-popover/carrier-search-popover.component';
import { ToggleableButtonComponent } from './toggleable-button/toggleable-button.component';
import { ProjectPopoverComponent } from './project-popover/project-popover.component';
import { CarrierRepPopoverComponent } from './carrier-rep-popover/carrier-rep-popover.component';
import { AccountExecPopoverComponent } from './account-exec-popover/account-exec-popover.component';
import { LoadsTableComponent } from './loads-table/loads-table.component';
import {
	CarrierSelectorDialog,
	CarrierSelectorDialogComponent,
} from './carrier-selector-dialog/carrier-selector-dialog.component';
import { ProjectDialog, ProjectDialogComponent } from './project-dialog/project-dialog.component';
import { ProjectTypeDialogComponent } from './project-type-dialog/project-type-dialog.component';
import { EditAccountDetailsDialogComponent } from './edit-account-details/edit-account-details-dialog.component';
import {
	BAUploadCarriersDialog,
	UploadCarriersComponent,
	UploadCarriersDialog,
} from './upload-carriers/upload-carriers.component';
import { FinalStepComponent } from './upload-carriers/final-step/final-step.component';
import { FirstStepComponent as FileUploadCarrierComponent } from './upload-carriers/first-step/first-step.component';

import { FrequencyFilterComponent } from './frequency-filter/frequency-filter.component';
import { PowerUnitsFilterComponent } from './power-units-filter/power-units-filter.component';

import { PricingComponent } from './pricing/pricing.component';
import { AddPriceReviewDialogComponent } from './add-price-review-dialog/add-price-review-modal';
import { RecentTransactionsDialogComponent } from './recent-transactions-dialog/recent-transactions-dialog';
import { ProjectsTabContentComponent } from './projects-tab-content/projects-tab-content.component';

import { NgDragDropModule } from 'ng-drag-drop';
import { SkeletonLoaderComponent } from './skeleton-loader/skeleton-loader.component';
import { AccountSearchComponent } from './account-search/account-search.component';

import { CarrierRateDialogComponent } from './carrier-rate-dialog/carrier-rate-dialog.component';
import { BaseComponent } from './base/base.component';
import { SafeHtmlPipe } from './safe-html.pipe';
import { CapacitySearchSettingsComponent } from '../admin/capacity-search-settings/capacity-search-settings.component';
import { MtxPopoverModule } from '@ng-matero/extensions/popover';
import { AccountAutocompleteComponent } from './account-autocomplete/account-autocomplete.component';
import { AccountEmailMapTableComponent } from './account-email-map-table/account-email-map-table.component';
import { AccountEmailMapDialogComponent } from './account-email-map-dialog/account-email-map-dialog.component';
import { ConfirmContactDialogComponent } from './confirm-contact-dialog/confirm-contact.component';
import { ConfirmTextDialogComponent } from './confirm-text-dialog/confirm-text-dialog.component';

import { TabbedTableComponent } from './tabbed-table/tabbed-table.component';
import { RunScriptsDirective } from './directives/run-scripts/run-scripts.directive';
import { TruncateTooltipDirective } from './directives/truncate-tooltip.directive';

import { MatLegacyTooltipModule as MatTooltipModule } from '@angular/material/legacy-tooltip';
import { ReadMoreComponent } from './read-more/read-more.component';
import { QualityComponent } from './quality/quality.component';
import { AddCarriersDialogComponent } from './add-carriers-dialog/add-carriers-dialog.component';
import { QualityStatsComponent } from './quality-stats/quality-stats.component';

const maskConfig: Partial<IConfig> = {};

const imports = [];

const components = [
	ColumnDetailsComponent,
	SpecialInstructionsDialog,
	SpecialInstructionsDialogComponent,
	EditPlanComponent,
	EditPlanDialog,
	BidMonitorLanesComponent,
	LanesDialog,
	AddLaneComponent,
	AddLaneDialog,
	CarrierLanesComponent,
	DestinationsComponent,
	LaneActionsComponent,
	SearchTruckBarComponent,
	DateRangeSelectorComponent,
	McDotFlipAnimationComponent,
	LoadingSpinnerDialogComponent,
	GoogleLocationDirective,
	NumbersInputDirective,
	ProjectSelectorComponent,
	CarrierAutocompleteComponent,
	CarrierSearchComponent,
	CarrierSelectorComponent,
	CustomerSelectorComponent,
	UserPopoverComponent,
	CustomerPopoverComponent,
	SetPasswordComponent,
	PricingFeedbackDialogComponent,
	SearchHorizontalListComponent,
	DeadheadSlidersComponent,
	ProjectPanelComponent,
	RequestForProposalComponent,
	RFPDialog,
	CarrierSearchPopoverComponent,
	ToggleableButtonComponent,
	ProjectPopoverComponent,
	CarrierRepPopoverComponent,
	AccountExecPopoverComponent,
	LoadsTableComponent,
	CarrierSelectorDialog,
	CarrierSelectorDialogComponent,
	ProjectDialogComponent,
	ProjectTypeDialogComponent,
	ProjectDialog,
	EditAccountDetailsDialogComponent,
	UploadCarriersComponent,
	UploadCarriersDialog,
	FinalStepComponent,
	FileUploadCarrierComponent,
	BAUploadCarriersDialog,
	FrequencyFilterComponent,
	PowerUnitsFilterComponent,
	PricingComponent,
	AddPriceReviewDialogComponent,
	RecentTransactionsDialogComponent,
	ProjectsTabContentComponent,
	SkeletonLoaderComponent,
	AccountSearchComponent,
	CarrierRateDialogComponent,
	BaseComponent,
	SafeHtmlPipe,
	CapacitySearchSettingsComponent,
	AccountAutocompleteComponent,
	AccountEmailMapTableComponent,
	AccountEmailMapDialogComponent,
	TabbedTableComponent,
	ConfirmContactDialogComponent,
	ConfirmTextDialogComponent,
	RunScriptsDirective,
	TruncateTooltipDirective,
	ReadMoreComponent,
	QualityComponent,
	AddCarriersDialogComponent,
	QualityStatsComponent,
];

@NgModule({
	declarations: [components],
	imports: [
		CommonModule,
		imports,
		CoreModule,
		NgxDaterangepickerMd.forRoot(),
		EllipsisModule,
		NgDragDropModule.forRoot(),
		MtxPopoverModule,
		MatTooltipModule,
		NgxMaskDirective,
		NgxMaskPipe,
	],
	exports: [...imports, ...components],
	providers: [provideEnvironmentNgxMask(maskConfig)],
})
export class SharedModule {}
